"use client"

import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import { IndianRupee, Users, Plus, Eye, Search, RefreshCw, ChevronRight, AlertTriangle, Clock, CheckCircle, Store } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { fetchCustomersList, createCustomer, fetchLedgerSummary } from "../../../services/ledgerService"

const RISK_COLORS = {
  CLEAR: "bg-green-100 text-green-700 border border-green-200",
  SAFE: "bg-green-100 text-green-700 border border-green-200",
  "DUE SOON": "bg-yellow-100 text-yellow-700 border border-yellow-200",
  PENDING: "bg-gray-100 text-gray-600 border border-gray-200",
  OVERDUE: "bg-red-100 text-red-700 border border-red-200",
  "NO TRANSACTION": "bg-gray-100 text-gray-500 border border-gray-200",
}

function fmtMoney(n) {
  return (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })
}

export default function CustomersList() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  const [filtered, setFiltered] = useState([])
  const [ledgerData, setLedgerData] = useState(new Map())
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState("")

  const [showAddCustomer, setShowAddCustomer] = useState(false)
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" })
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      // Fetch all customers
      const customersRes = await fetchCustomersList({ limit: 500 })
      const customersList = customersRes.data || []

      // Fetch ledger summary for all customers
      const ledgerRes = await fetchLedgerSummary()
      const ledgerSummary = ledgerRes.data || []

      // Create lookup map for ledger data
      const ledgerMap = new Map()
      ledgerSummary.forEach((item) => {
        ledgerMap.set(item.customer_id?.toString() || item._id?.toString(), item)
      })

      setCustomers(customersList)
      setFiltered(customersList)
      setLedgerData(ledgerMap)
    } catch {
      toast.error("Failed to load customers")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(customers)
      return
    }
    const q = search.toLowerCase()
    setFiltered(customers.filter((c) =>
      c.name?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    ))
  }, [search, customers])

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error("Customer name is required")
      return
    }
    // Validate phone format (optional) and prevent duplicate phone numbers client-side for quicker feedback
    const phoneRaw = (newCustomer.phone || "").trim()
    const phoneNormalized = phoneRaw.replace(/[^+0-9]/g, "")
    if (phoneRaw) {
      const digits = phoneNormalized.replace(/[^0-9]/g, "")
      if (digits.length < 7 || digits.length > 15) {
        toast.error("Phone number must have between 7 and 15 digits")
        return
      }
      if (customers.some(c => ((c.phone || "").replace(/[^0-9]/g, "") === digits))) {
        toast.error("Phone number already in use by another customer")
        return
      }
      // store normalized phone
      newCustomer.phone = phoneNormalized
    }
    try {
      setSaving(true)
      await createCustomer(newCustomer)
      toast.success("Customer added")
      setShowAddCustomer(false)
      setNewCustomer({ name: "", phone: "", email: "" })
      load()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add customer")
    } finally {
      setSaving(false)
    }
  }

  // Get customer status with risk indicator
  const getCustomerStatus = (customer) => {
    const ledger = ledgerData.get(customer._id?.toString())

    if (!ledger || ledger.total_transactions === 0) {
      return { label: "NO TRANSACTION", color: "gray", icon: Store, due: 0 }
    }

    const due = ledger.total_due || 0
    if (due <= 0) {
      return { label: "PAID", color: "green", icon: CheckCircle, due: 0 }
    }

    // Has due - check risk
    const risk = ledger.risk
    if (!risk) {
      return { label: "PENDING", color: "gray", icon: Clock, due }
    }

    const { overdue_count, due_soon_count } = risk
    if (overdue_count > 0) {
      return { label: "OVERDUE", color: "red", icon: AlertTriangle, due }
    }
    if (due_soon_count > 0) {
      return { label: "DUE SOON", color: "yellow", icon: Clock, due }
    }
    return { label: "SAFE", color: "green", icon: CheckCircle, due }
  }

  const totalCustomers = customers.length
  const customersWithDue = Array.from(ledgerData.values()).filter((l) => l.total_due > 0).length
  const overdueCustomers = Array.from(ledgerData.values()).filter(
    (l) => l.total_due > 0 && l.risk?.overdue_count > 0
  ).length
  const overallOutstanding = Array.from(ledgerData.values()).reduce((sum, l) => sum + (l.total_due || 0), 0)

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage customers and view their ledger status</p>
        </div>
        <Button onClick={() => setShowAddCustomer(true)} className="flex items-center gap-2 w-fit">
          <Plus className="w-4 h-4" />
          Add Customer
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="bg-blue-50 rounded-lg p-2.5">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Total Customers</p>
            <p className="text-2xl font-bold text-gray-900">{totalCustomers}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="bg-red-50 rounded-lg p-2.5">
            <IndianRupee className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">With Outstanding</p>
            <p className="text-2xl font-bold text-gray-900">{customersWithDue}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="bg-red-100 rounded-lg p-2.5">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Overdue</p>
            <p className="text-2xl font-bold text-red-600">{overdueCustomers}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-4">
          <div className="bg-orange-50 rounded-lg p-2.5">
            <IndianRupee className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 font-medium">Total Outstanding</p>
            <p className="text-2xl font-bold text-red-600">₹{fmtMoney(overallOutstanding)}</p>
          </div>
        </div>
      </div>

      {/* Search + Refresh */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, phone, or email..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button variant="outline" size="icon" onClick={load} title="Refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">
            {search ? "No customers match your search." : "No customers yet. Add one to get started."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Due</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Transactions</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((customer) => {
                  const status = getCustomerStatus(customer)
                  const StatusIcon = status.icon
                  const ledger = ledgerData.get(customer._id?.toString())

                  return (
                    <tr key={customer._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{customer.name}</p>
                        {customer.email && <p className="text-xs text-gray-400">{customer.email}</p>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{customer.phone || "—"}</td>
                      <td className="px-4 py-3 text-right font-semibold text-red-600">
                        {status.due > 0 ? `₹${fmtMoney(status.due)}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[status.label]}`}>
                          <StatusIcon className="w-3 h-3" />
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-600">
                        {ledger?.total_transactions || 0}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                          onClick={() => navigate(`/dashboard/inventory/ledger/${customer._id}`)}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">View Ledger</span>
                          <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                placeholder="Customer name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                placeholder="Phone number"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                placeholder="Email address"
                type="email"
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCustomer(false)}>Cancel</Button>
            <Button onClick={handleAddCustomer} disabled={saving}>
              {saving ? "Saving..." : "Add Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
